<script setup>
import { storeToRefs } from 'pinia';
import { computed, onBeforeUnmount, ref } from 'vue';

import { api } from '@/api/client.js';
import AnalyticsChart from '@/components/charts/AnalyticsChart.vue';
import PanelCard from '@/components/panels/PanelCard.vue';
import { usePanelState } from '@/composables/usePanelState.js';
import { usePoll } from '@/composables/usePoll.js';
import { useTenantStore } from '@/stores/tenant.js';

const tenant = useTenantStore();
const { activeAppId, timeWindow } = storeToRefs(tenant);
const ps = usePanelState('loading');
const sources = ref([]);
const selectedSourceKey = ref('');
const selected = ref(null);
const bins = ref([]);
let sourceRequestController = null;
let sourceSelectionVersion = 0;
let sourceRefreshTimer = null;

function combinedSignal(signal, controller) {
  if (typeof AbortSignal.any === 'function') return AbortSignal.any([signal, controller.signal]);
  signal.addEventListener('abort', () => controller.abort(signal.reason), { once: true });
  return controller.signal;
}

function queueSourceRefresh() {
  if (sourceRefreshTimer) return;
  sourceRefreshTimer = setTimeout(() => {
    sourceRefreshTimer = null;
    tick();
  }, 0);
}

function isInfraV2(source) {
  return source?.schemaVersion === 2;
}

async function fetchInfraV2Sources({ appId, signal }) {
  const loaded = [];
  const cursors = new Set();
  let after;
  for (let page = 0; page < 10; page += 1) {
    const response = await api.events.probeInfraSources({ appId, after, signal });
    if (response?.ok !== true || !Array.isArray(response.sources)) {
      throw new Error('Invalid Infra v2 sources response');
    }
    loaded.push(...response.sources);
    const nextAfter = response.nextAfter ?? null;
    if (!nextAfter) return loaded;
    if (cursors.has(nextAfter)) throw new Error('Infra v2 source pagination cursor repeated');
    cursors.add(nextAfter);
    after = nextAfter;
  }
  throw new Error('Infra v2 source pagination exceeded 10 pages');
}

async function fetchFn({ signal }) {
  if (!activeAppId.value) return null;
  const version = sourceSelectionVersion;
  const appId = activeAppId.value;
  const window = timeWindow.value;
  const controller = new AbortController();
  sourceRequestController = controller;
  const requestSignal = combinedSignal(signal, controller);
  const [sourceResponse, infraV2Sources] = await Promise.all([
    api.events.hostHealthSources({ appId, signal: requestSignal }),
    fetchInfraV2Sources({ appId, signal: requestSignal }),
  ]);
  if (sourceResponse?.ok !== true || !Array.isArray(sourceResponse.sources)) throw new Error('Invalid host telemetry sources response');
  // v2 is the default when present, while v1 remains selectable for
  // historical continuity. Metrics are always shown for one source only.
  const available = [...infraV2Sources, ...sourceResponse.sources];
  const source = available.find((item) => sourceKey(item) === selectedSourceKey.value)
    ?? available.find(isInfraV2)
    ?? available[0]
    ?? null;
  if (!source) return { version, sourceKey: selectedSourceKey.value, sources: available, selected: null, bins: [] };
  const seriesResponse = isInfraV2(source)
    ? await api.events.probeInfraSeries({
      appId,
      resourceId: source.resourceId,
      probeId: source.probeId,
      window,
      signal: requestSignal,
    })
    : await api.events.hostHealthSeries({
      appId,
      environment: source.environment,
      sourceId: source.sourceId,
      window,
      signal: requestSignal,
    });
  if (seriesResponse?.ok !== true || !Array.isArray(seriesResponse.bins)) throw new Error('Invalid host telemetry series response');
  return {
    version,
    sourceKey: sourceKey(source),
    sources: available,
    selected: { ...source, ...seriesResponse.source, state: source.state },
    bins: seriesResponse.bins,
  };
}

const { tick, status: refreshStatus, lastUpdatedAt } = usePoll(fetchFn, {
  watch: [() => tenant.changeKey],
  onContextReset() {
    sourceSelectionVersion += 1;
    sourceRequestController?.abort();
    sources.value = []; selected.value = null; selectedSourceKey.value = ''; bins.value = [];
    ps.setState('loading');
  },
  onSuccess(result) {
    if (result?.version !== sourceSelectionVersion) {
      queueSourceRefresh();
      return;
    }
    sources.value = result?.sources ?? [];
    selected.value = result?.selected ?? null;
    selectedSourceKey.value = selected.value ? sourceKey(selected.value) : '';
    bins.value = result?.bins ?? [];
    ps.setState(selected.value ? 'data' : 'empty');
  },
  onError(cause) {
    ps.setState('error', { message: 'Could not load host telemetry', detail: cause?.message ?? '' });
  },
});

onBeforeUnmount(() => {
  clearTimeout(sourceRefreshTimer);
  sourceRequestController?.abort();
});

function selectSource() {
  sourceSelectionVersion += 1;
  sourceRequestController?.abort();
  ps.setState('loading');
  queueSourceRefresh();
}

function sourceKey(source) {
  if (isInfraV2(source)) return `v2:${source.resourceId}:${source.probeId}`;
  return `${source.environment}:${source.sourceId}`;
}

function sourceLabel(source) {
  if (isInfraV2(source)) {
    const environments = Array.isArray(source.environments) && source.environments.length > 0
      ? source.environments.join(', ')
      : 'no visible environments';
    return `${source.resourceName || source.resourceId} · ${source.probeName || source.probeId} · ${environments} · Infra v2`;
  }
  return `${source.sourceId} · ${source.environment} · ${source.scope} · Historical v1`;
}

ps.setRetryHandler(() => tick());
defineExpose({ ps, refreshStatus });

function latest(metricName) {
  const metric = selected.value?.latestMetrics?.[metricName];
  const status = selected.value?.metrics?.[metricName] ?? metric?.status;
  return status === 'ok' ? metric : null;
}

function percent(metric) {
  if (!metric) return 'Unavailable';
  return `${Math.round((metric.utilizationRatio ?? (metric.usedBytes / metric.totalBytes)) * 100)}%`;
}

function rate(value) {
  if (!Number.isFinite(value)) return 'Unavailable';
  const units = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
  let scaled = value;
  let unit = 0;
  while (scaled >= 1024 && unit < units.length - 1) { scaled /= 1024; unit += 1; }
  return `${scaled >= 10 || unit === 0 ? Math.round(scaled) : scaled.toFixed(1)} ${units[unit]}`;
}

const cpu = computed(() => latest('cpu'));
const memory = computed(() => latest('memory'));
const disk = computed(() => latest('disk'));
const network = computed(() => {
  if (!latest('network')) return null;
  const current = bins.value.find((bin) => bin.sampledAt === selected.value?.sampledAt)?.network;
  return current?.status === 'ok' ? current : null;
});
const labels = computed(() => bins.value.map(({ at }) => {
  const date = new Date(at);
  return Number.isNaN(date.getTime()) ? at : date.toLocaleString(undefined, ['1h', '24h'].includes(timeWindow.value) ? { hour: '2-digit', minute: '2-digit' } : { month: 'short', day: 'numeric', hour: timeWindow.value === '7d' ? '2-digit' : undefined });
}));
const cpuDataset = computed(() => [{ label: 'CPU %', data: bins.value.map((bin) => bin.cpu?.status === 'ok' ? Math.round(bin.cpu.utilizationRatio * 100) : null), borderColor: 'var(--accent-fg-on-soft)' }]);
const memoryDataset = computed(() => [{ label: 'RAM %', data: bins.value.map((bin) => bin.memory?.status === 'ok' ? Math.round((bin.memory.usedBytes / bin.memory.totalBytes) * 100) : null), borderColor: 'var(--success-icon)' }]);
const diskDataset = computed(() => [{ label: 'Disk %', data: bins.value.map((bin) => bin.disk?.status === 'ok' ? Math.round((bin.disk.usedBytes / bin.disk.totalBytes) * 100) : null), borderColor: 'var(--warning-icon)' }]);
const networkDataset = computed(() => [
  { label: 'Receive B/s', data: bins.value.map((bin) => bin.network?.status === 'ok' ? bin.network.rxBytesPerSec : null), borderColor: 'var(--accent-fg-on-soft)' },
  { label: 'Transmit B/s', data: bins.value.map((bin) => bin.network?.status === 'ok' ? bin.network.txBytesPerSec : null), borderColor: 'var(--fg-secondary)' },
]);
const availability = computed(() => ({
  cpu: Boolean(cpu.value), memory: Boolean(memory.value), disk: Boolean(disk.value), network: Boolean(network.value),
}));
const measuredAt = computed(() => selected.value?.measuredAt ?? selected.value?.sampledAt ?? null);
const inventoryHref = computed(() => {
  if (!isInfraV2(selected.value) || !activeAppId.value) return null;
  const query = new URLSearchParams({appId: activeAppId.value, resourceId: selected.value.resourceId, probeId: selected.value.probeId});
  return `/probes?${query.toString()}`;
});
const relatedQuery = computed(() => ({appId:activeAppId.value,
  window:['1h','24h','7d'].includes(timeWindow.value)?timeWindow.value:'24h'}));
const relatedHref = (path) => `${path}?${new URLSearchParams(relatedQuery.value).toString()}`;
const e2eHref = computed(() => activeAppId.value
  ? `/apps/${encodeURIComponent(activeAppId.value)}/e2e?${new URLSearchParams({window:relatedQuery.value.window})}` : null);
</script>

<template>
  <PanelCard
    title="Host telemetry"
    :state="ps.state.value"
    empty-headline="No host telemetry yet"
    empty-body="A host appears here after it publishes a health sample."
    :error-message="ps.errorMessage.value"
    :error-detail="ps.errorDetail.value"
    :on-retry="ps.retry"
    :refresh-status="refreshStatus"
    :last-updated-at="lastUpdatedAt"
  >
    <template #header>
      <label class="host-health__source">
        <span class="visually-hidden">Host source</span>
        <select
          v-model="selectedSourceKey"
          aria-label="Host source"
          @change="selectSource"
        >
          <option
            v-for="source in sources"
            :key="sourceKey(source)"
            :value="sourceKey(source)"
          >
            {{ sourceLabel(source) }}
          </option>
        </select>
      </label>
      <a
        v-if="inventoryHref"
        class="host-health__inventory-link"
        :href="inventoryHref"
      >View probe inventory</a>
      <a v-if="activeAppId" class="host-health__inventory-link" :href="relatedHref('/probes/heartbeat')">Heartbeat</a>
      <a v-if="activeAppId" class="host-health__inventory-link" :href="relatedHref('/probes/logs')">Logs</a>
      <a v-if="e2eHref" class="host-health__inventory-link" :href="e2eHref">E2E tests</a>
    </template>

    <p
      v-if="isInfraV2(selected)"
      class="host-health__measured"
    >
      Physical or shared host telemetry. CPU, RAM, disk, and network measurements describe the host and are not attributed to an application.
    </p>

    <p
      v-if="selected?.state === 'stale'"
      class="host-health__notice"
      role="status"
    >
      Telemetry is stale. Last sample {{ selected.sampledAt ? new Date(selected.sampledAt).toLocaleString() : 'is unavailable' }}.
    </p>
    <p
      v-if="selected?.state === 'no-data'"
      class="host-health__notice"
      role="status"
    >
      This source has not published a usable sample yet.
    </p>
    <p
      v-if="measuredAt"
      class="host-health__measured"
    >
      Latest source sample: {{ new Date(measuredAt).toLocaleString() }}.
    </p>

    <p class="host-health__measured">
      History is retained for 30 days. Older intervals contain no samples.
    </p>

    <dl class="host-health__metrics">
      <div><dt>CPU</dt><dd>{{ percent(cpu) }}</dd><small>{{ availability.cpu ? 'Latest utilization' : 'Unavailable' }}</small></div>
      <div><dt>RAM</dt><dd>{{ percent(memory) }}</dd><small>{{ availability.memory ? 'Latest utilization' : 'Unavailable' }}</small></div>
      <div><dt>Disk</dt><dd>{{ percent(disk) }}</dd><small>{{ availability.disk ? 'Latest utilization' : 'Unavailable' }}</small></div>
      <div><dt>Network</dt><dd>{{ network ? `${rate(network.rxBytesPerSec)} ↓ / ${rate(network.txBytesPerSec)} ↑` : 'Unavailable' }}</dd><small>{{ availability.network ? 'Receive / transmit' : 'Unavailable' }}</small></div>
    </dl>

    <div class="host-health__charts">
      <section>
        <h3>CPU utilization</h3><AnalyticsChart
          type="line"
          :labels="labels"
          :datasets="cpuDataset"
          aria-label="CPU utilization over time"
        />
      </section>
      <section>
        <h3>RAM utilization</h3><AnalyticsChart
          type="line"
          :labels="labels"
          :datasets="memoryDataset"
          aria-label="RAM utilization over time"
        />
      </section>
      <section>
        <h3>Disk utilization</h3><AnalyticsChart
          type="line"
          :labels="labels"
          :datasets="diskDataset"
          aria-label="Disk utilization over time"
        />
      </section>
      <section>
        <h3>Network rate</h3><AnalyticsChart
          type="line"
          :labels="labels"
          :datasets="networkDataset"
          aria-label="Network receive and transmit rate over time"
        />
      </section>
    </div>
    <template #tableFallback>
      <table class="host-health__table">
        <caption class="visually-hidden">
          Host telemetry by time bin
        </caption>
        <thead>
          <tr>
            <th scope="col">
              Time
            </th><th scope="col">
              CPU
            </th><th scope="col">
              RAM
            </th><th scope="col">
              Disk
            </th><th scope="col">
              Network
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="bin in bins"
            :key="bin.at"
          >
            <td>{{ bin.at }}</td><td>{{ bin.cpu.status === 'ok' ? `${Math.round(bin.cpu.utilizationRatio * 100)}%` : 'Unavailable' }}</td><td>{{ bin.memory.status === 'ok' ? `${Math.round(bin.memory.usedBytes / bin.memory.totalBytes * 100)}%` : 'Unavailable' }}</td><td>{{ bin.disk.status === 'ok' ? `${Math.round(bin.disk.usedBytes / bin.disk.totalBytes * 100)}%` : 'Unavailable' }}</td><td>{{ bin.network.status === 'ok' ? `${rate(bin.network.rxBytesPerSec)} ↓ / ${rate(bin.network.txBytesPerSec)} ↑` : 'Unavailable' }}</td>
          </tr>
        </tbody>
      </table>
    </template>
  </PanelCard>
</template>

<style scoped>
.host-health__source select {
  min-height: var(--control-md);
  padding: 0 var(--space-3);
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--bg-canvas);
  color: var(--fg-primary);
  font: inherit;
  font-size: var(--text-sm);
}

.host-health__inventory-link {
  display: inline-flex;
  align-items: center;
  min-height: 2rem;
  margin-left: var(--space-2);
  color: var(--accent-fg-on-soft);
  font-size: var(--text-sm);
  font-weight: 600;
}

.host-health__notice {
  margin: 0 0 var(--space-4);
  color: var(--warning-fg);
  font-size: var(--text-sm);
}

.host-health__measured {
  margin: 0 0 var(--space-4);
  color: var(--fg-secondary);
  font-size: var(--text-xs);
}

.host-health__metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--space-4);
  margin: 0 0 var(--space-5);
}

.host-health__metrics div {
  min-width: 0;
}

.host-health__metrics dt,
.host-health__metrics small {
  color: var(--fg-secondary);
  font-size: var(--text-xs);
}

.host-health__metrics dd {
  margin: var(--space-1) 0;
  font-size: var(--text-xl);
  font-variant-numeric: tabular-nums;
}

.host-health__charts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-5);
}

.host-health__charts section {
  min-width: 0;
  padding: var(--space-4);
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-xl);
}

.host-health__charts h3 {
  margin: 0 0 var(--space-3);
  color: var(--fg-secondary);
  font-size: var(--text-sm);
}

.host-health__table {
  width: 100%;
  border-collapse: collapse;
}

.host-health__table th,
.host-health__table td {
  padding: var(--space-2);
  border-bottom: var(--bw-thin) solid var(--border-default);
  text-align: left;
}

@media (width <= 767px) {
  .host-health__metrics,
  .host-health__charts {
    grid-template-columns: 1fr;
  }
}
</style>
